import moment from "moment";
import { formatToCurrency } from "shared/utils";
import type { FraudAlert, FraudAlertStatus } from "api/propertyShield";

// Property Shield: "active" is confirmed fraud still online (the partner reported it);
// "false_red_flag" is the partner's own legitimate listing. False matches never reach the portal.
export const FRAUD_ALERT_STATUS_LABEL: Record<FraudAlertStatus, string> = {
  pending_confirmation: "Needs review",
  active: "Reported",
  false_red_flag: "Dismissed",
  removed: "Removed"
};

/** Vendor URLs: only let http(s) through to `href` / `window.open`. */
export const safeExternalUrl = (url?: string | null): string | null => {
  if (!url) return null;
  try {
    const { protocol } = new URL(url);
    return protocol === "http:" || protocol === "https:" ? url : null;
  } catch {
    return null;
  }
};

/** Review/report/dismiss lives on Property Shield's page; Boom only links out. */
export const fraudAlertReviewUrl = (alert: FraudAlert) =>
  safeExternalUrl(alert.property_shield_url) ??
  safeExternalUrl(alert.listing_url);

export const fraudAlertReviewLabel = (alert: FraudAlert) =>
  safeExternalUrl(alert.property_shield_url)
    ? "Review on Property Shield"
    : "View listing";

export const formatFraudAlertListedFor = (alert: FraudAlert) =>
  alert.listed_for ? formatToCurrency(alert.listed_for) : "—";

export const formatFraudAlertDate = (value: string | null) =>
  value ? moment(value).format("MMM D, YYYY") : "—";

export const openFraudAlertReview = (alert: FraudAlert) => {
  const url = fraudAlertReviewUrl(alert);
  if (url) window.open(url, "_blank", "noopener,noreferrer");
};

export const unitFraudAlertsPath = (propertyId: number, unitId: number) =>
  `/properties/${propertyId}/units/${unitId}?tab=fraud-alerts`;

export const fraudAlertTitle = (platform: string) =>
  platform ? `Copied listing on ${platform}` : "Copied listing";

/** "City, ST" from the copied post's location ("street, city, ST zip"); null when it has no city. */
export const fraudAlertCityState = (location?: string | null) => {
  const parts = (location ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 3) return null;

  const city = parts[parts.length - 2].replace(/\b\w/g, (c) => c.toUpperCase());
  const state = parts[parts.length - 1].split(/\s+/)[0]?.toUpperCase();
  return [city, state].filter(Boolean).join(", ");
};

/** Host + path, for showing a listing URL compactly. */
export const shortListingUrl = (url: string) => {
  try {
    const { host, pathname } = new URL(url);
    return `${host.replace(/^www\./, "")}${pathname === "/" ? "" : pathname}`;
  } catch {
    return url;
  }
};
